import { Draft, PayloadAction, createSlice } from '@reduxjs/toolkit';
import semver from 'semver';
import { server, user } from 'stardog';
import { heapAgent } from 'vet-bones/utils';

import {
  Connection,
  ConnectionFailureReason,
  ConnectionStatus,
  ConnectionsState,
  createInitialConnectionsState,
} from 'src/store/connections/types';
import { createStardogAsyncThunk } from 'src/utils/request/createStardogAsyncThunk';

export const connectionAsyncActions = {
  requestCurrentPermissions: createStardogAsyncThunk<{ username: string }>(
    'connections/requestCurrentPermissions',
    // Returns *effective* permissions, role-inherited ones included.
    ({ connection, username }) => user.get(connection, username),
    { agent: 'Designer' }
  ),
  requestCurrentUser: createStardogAsyncThunk(
    'connections/requestCurrentUser',
    ({ connection }) => user.whoAmI(connection),
    { agent: 'Designer' }
  ),
  requestServerStatus: createStardogAsyncThunk<{
    connection: Connection;
    params?: Parameters<typeof server.status>[1];
  }>(
    'connections/requestServerStatus',
    ({ connection, params }) => server.status(connection, params),
    { agent: 'Designer' }
  ),
  requestServerProperties: createStardogAsyncThunk<{
    connection: Connection;
    params?: Parameters<typeof server.properties>[1];
  }>(
    'connections/requestServerProperties',
    ({ connection, params }) => server.properties(connection, params),
    { agent: 'Designer' }
  ),
  requestValidateConnection: createStardogAsyncThunk<{
    shouldNotNotifySuccess?: boolean;
  }>(
    'connections/requestValidateConnection',
    ({ connection }) => user.valid(connection),
    { agent: 'Designer' }
  ),
};

/** A stale state here would apply the prior connection's permissions to the next user. */
const clearPermissions = (state: Draft<ConnectionsState>) => {
  state.permissions = null;
  state.permissionsUnavailable = false;
};

const clearConnectionPermissions = (state: Draft<ConnectionsState>) => {
  clearPermissions(state);
  state.authenticatedUsername = null;
};

export const connectionsSlice = createSlice({
  name: 'connections',
  initialState: createInitialConnectionsState,
  reducers: {
    connectToStardogFinished: (state) => {
      state.pendingConnectToStardog = false;
    },
    connectToStardogStarted: (state) => {
      clearConnectionPermissions(state);
      state.pendingConnectToStardog = true;
    },
    disconnected: (state) => {
      heapAgent.resetEventProperties();
      state.failureReason = null;
      clearConnectionPermissions(state);
      state.status = null;
    },
    /** No username to look permissions up by. */
    permissionsLookupSkipped: (state) => {
      state.permissions = null;
      state.permissionsUnavailable = true;
    },
    signedOut: (
      state,
      action: PayloadAction<{
        connection: Connection;
        /**
         * Whether to show the `SignInDialog` or not.
         *
         * When false we just setup connection on state.
         */
        showDialog?: boolean;
      }>
    ) => {
      heapAgent.resetEventProperties();
      state.connection = { ...action.payload.connection };
      state.failureReason = null;
      clearConnectionPermissions(state);
      state.status = ConnectionStatus.SIGNED_OUT;
    },
    authenticatedUsernameResolved: (state, action: PayloadAction<string>) => {
      state.authenticatedUsername = action.payload;
    },
    updatedConnectionToken: (
      state,
      action: PayloadAction<{ token: string }>
    ) => {
      if (state.connection?.token) {
        state.connection.token = action.payload.token;
      }
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(
      connectionAsyncActions.requestServerStatus.fulfilled,
      (state, action) => {
        const { connection } = action.meta.arg;
        const rawVersion = action.payload.body?.['dbms.version']?.value;
        const voiceboxDbLimit: number =
          action.payload.body?.['dbms.license.voicebox.limit']?.value ?? 0;
        const stardogLicenseType =
          action.payload.body?.['dbms.license.type']?.value;
        heapAgent.resetEventProperties({
          stardogVersion: rawVersion || undefined,
          stardogLicenseType,
        });
        state.stardogVersionByConnectionId[connection.id] = rawVersion ?? null;
        state.featureVersionByConnectionId[connection.id] =
          semver.coerce(rawVersion)?.version ?? null;
        state.voiceboxDbLimitByConnectionId[connection.id] = voiceboxDbLimit;
      }
    );
    addCase(
      connectionAsyncActions.requestServerProperties.fulfilled,
      (state, action) => {
        const { connection } = action.meta.arg;
        const catalogDatabase: string | null =
          action.payload.body?.['catalog.database'];
        state.catalogDatabaseByConnectionId[connection.id] =
          catalogDatabase || 'catalog';
        const querylogDatabase: string | null =
          action.payload.body?.['dbms.query.log.database'];
        state.querylogDatabaseByConnectionId[connection.id] =
          querylogDatabase || 'querylog';
      }
    );
    addCase(
      connectionAsyncActions.requestValidateConnection.pending,
      (state, action) => {
        const { connection } = action.meta.arg;
        if (connection) {
          state.connection = { ...connection };
          state.failureReason = null;
          clearConnectionPermissions(state);
          state.status = ConnectionStatus.PENDING;
        }
      }
    );
    addCase(
      connectionAsyncActions.requestValidateConnection.rejected,
      (state, action) => {
        const status = action.payload?.response?.status;
        state.failureReason =
          status === undefined ? ConnectionFailureReason.NO_RESPONSE : null;
        clearConnectionPermissions(state);
        state.status = ConnectionStatus.FAILURE;
      }
    );
    addCase(
      connectionAsyncActions.requestValidateConnection.fulfilled,
      (state, action) => {
        const { connection } = action.meta.arg;
        if (connection) {
          state.connection = { ...connection };
          clearConnectionPermissions(state);
          state.status = ConnectionStatus.SUCCESS;
        }
      }
    );
    addCase(
      connectionAsyncActions.requestCurrentPermissions.fulfilled,
      (state, action) => {
        state.permissions = action.payload.body?.permissions ?? [];
        state.permissionsUnavailable = false;
      }
    );
    addCase(
      connectionAsyncActions.requestCurrentPermissions.pending,
      clearPermissions
    );
    addCase(
      connectionAsyncActions.requestCurrentPermissions.rejected,
      (state) => {
        // Not the same as null: that reads as "still loading" and locks the UI.
        state.permissions = null;
        state.permissionsUnavailable = true;
      }
    );
  },
});
